import { useEffect } from 'react'

import { useAuth, useUser } from '@clerk/clerk-react'
import { datadogRum } from '@datadog/browser-rum'
import posthog from 'posthog-js'
import { ModuleType as ModuleTypeEnum } from 'types/graphql'

import { useLocation } from '@cedarjs/router'

import { DatadogSettings, EnvVariables } from 'src/constants'
import { useLogSentEmailActivityMutation } from 'src/fetch/sentEmailActivities'
import { usePersistentIds } from 'src/hooks/use-persistent-ids'
import { AppStateContextData } from 'src/layouts/AppStateContextLayout/types'
import { MapModeType } from 'src/types'

export enum MonitoringEvent {
  AiHotTopicClicked = 'ai_hot_topic_clicked',
  AutoCheckEnableForApplicantsBannerDismissed = 'auto_check_enable_for_applicants_banner_dismissed',
  AutoCheckEnableForApplicantsBannerEnableClicked = 'auto_check_enable_for_applicants_banner_enable_clicked',
  AutoCheckEnableForApplicantsBannerShown = 'auto_check_enable_for_applicants_banner_shown',
  AutoCheckEnableForApplicantsFeedbackSubmitted = 'auto_check_enable_for_applicants_feedback_submitted',
  AutoCheckEnableForApplicantsModalDismissed = 'auto_check_enable_for_applicants_modal_dismissed',
  AutoCheckEnableForApplicantsModalEnableClicked = 'auto_check_enable_for_applicants_modal_enable_clicked',
  AutoCheckEnableForApplicantsModalShown = 'auto_check_enable_for_applicants_modal_shown',
  AutoCheckFeedbackAddAsCommentClicked = 'auto_check_feedback_add_as_comment_clicked',
  AutoCheckFeedbackAddedAsComment = 'auto_check_feedback_added_as_comment',
  AutoCheckFeedbackContentModifiedInComment = 'auto_check_feedback_content_modified_in_comment',
  AutoCheckFeedbackHovered = 'auto_check_feedback_hovered',
  AutoCheckFeedbackMarkedNotApplicable = 'auto_check_feedback_marked_not_applicable',
  AutoCheckFeedbackNotApplicableClicked = 'auto_check_feedback_not_applicable_clicked',
  AutoCheckFeedbackResolved = 'auto_check_feedback_resolved',
  CreateModuleClick = 'create_module_click',
  EntityVersionSearchItemSelected = 'entity_version_search_item_selected',
  MobileAppBannerDownloadClicked = 'mobile_app_banner_download_clicked',
  EntityVersionSearchMapLaunch = 'entity_version_search_map_launch',
  OrganizationPageView = 'organization_page_view',
  PlanReviewDiffModeLaunched = 'plan_review_diff_mode_launched',
  PlanReviewExportDownloaded = 'plan_review_export_downloaded',
  PlanReviewExportModalOpened = 'plan_review_export_modal_opened',
  PlanReviewExportPrinted = 'plan_review_export_printed',
  PlanReviewOpened = 'plan_review_opened',
  SettlementReportDownloaded = 'settlement_report_downloaded',
  SettlementReportShared = 'settlement_report_shared',
}

export enum MonitoringContextKey {
  AiChatSessionId = 'aiChatSessionId',
  AiTopicId = 'aiTopicId',
  AutoCheckEnableForApplicantsSource = 'autoCheckEnableForApplicantsSource',
  AutoCheckFeedbackItemId = 'autoCheckFeedbackItemId',
  AutoCheckFeedbackSource = 'autoCheckFeedbackSource',
  EntityVersionSearchMapModeType = 'entityVersionSearchMapModeType',
  IsEntityVersionMapMode = 'isEntityVersionMapMode',
  IsMultiSelect = 'isMultiSelect',
  IsProduction = 'isProduction',
  ModuleType = 'moduleType',
  MyOrganization = 'myOrganization',
  OrganizationByPathId = 'organizationByPathId',
  OrganizationByPathSlug = 'organizationByPathSlug',
  PendingAutoCheckItemCount = 'pendingAutoCheckItemCount',
  PlanReviewExportCommentCount = 'planReviewExportCommentCount',
  PlanReviewExportFileCount = 'planReviewExportFileCount',
  PlanReviewExportIncludeComments = 'planReviewExportIncludeComments',
  PlanReviewExportIncludeContacts = 'planReviewExportIncludeContacts',
  PlanReviewExportIncludeMeasurements = 'planReviewExportIncludeMeasurements',
  PlanReviewExportIncludeShapes = 'planReviewExportIncludeShapes',
  PlanReviewExportIncludeStamps = 'planReviewExportIncludeStamps',
  PlanReviewExportIncludeTextAnnotations = 'planReviewExportIncludeTextAnnotations',
  PlanReviewExportMeasurementCount = 'planReviewExportMeasurementCount',
  PlanReviewExportRawFilesOnly = 'planReviewExportRawFilesOnly',
  PlanReviewExportShapeCount = 'planReviewExportShapeCount',
  PlanReviewExportStampCount = 'planReviewExportStampCount',
  PlanReviewExportTextAnnotationCount = 'planReviewExportTextAnnotationCount',
  PlanReviewExportTotalFileCount = 'planReviewExportTotalFileCount',
  Recipients = 'recipients',
  RecordPlansSetReviewId = 'recordPlansSetReviewId',
  RecordTypeId = 'recordTypeId',
  SettlementId = 'settlementId',
}

export type AutoCheckFeedbackSource = 'PlanReview' | 'RecordDetails'

export type AutoCheckEnableForApplicantsSource =
  | 'banner'
  | 'settings'
  | 'in_context'
  | 'milestone'
  | 'digest_email'

type MonitoringContextValue = {
  [MonitoringContextKey.AiChatSessionId]: string
  [MonitoringContextKey.AiTopicId]: string
  [MonitoringContextKey.AutoCheckEnableForApplicantsSource]: AutoCheckEnableForApplicantsSource
  [MonitoringContextKey.AutoCheckFeedbackItemId]: number
  [MonitoringContextKey.AutoCheckFeedbackSource]: AutoCheckFeedbackSource
  [MonitoringContextKey.EntityVersionSearchMapModeType]: MapModeType
  [MonitoringContextKey.IsEntityVersionMapMode]: boolean
  [MonitoringContextKey.IsMultiSelect]: boolean
  [MonitoringContextKey.IsProduction]: boolean
  [MonitoringContextKey.ModuleType]: ModuleTypeEnum
  [MonitoringContextKey.OrganizationByPathId]: number
  [MonitoringContextKey.OrganizationByPathSlug]: string
  [MonitoringContextKey.PendingAutoCheckItemCount]: number
  [MonitoringContextKey.PlanReviewExportCommentCount]: number
  [MonitoringContextKey.PlanReviewExportFileCount]: number
  [MonitoringContextKey.PlanReviewExportIncludeComments]: boolean
  [MonitoringContextKey.PlanReviewExportIncludeContacts]: boolean
  [MonitoringContextKey.PlanReviewExportIncludeMeasurements]: boolean
  [MonitoringContextKey.PlanReviewExportIncludeShapes]: boolean
  [MonitoringContextKey.PlanReviewExportIncludeStamps]: boolean
  [MonitoringContextKey.PlanReviewExportIncludeTextAnnotations]: boolean
  [MonitoringContextKey.PlanReviewExportMeasurementCount]: number
  [MonitoringContextKey.PlanReviewExportRawFilesOnly]: boolean
  [MonitoringContextKey.PlanReviewExportShapeCount]: number
  [MonitoringContextKey.PlanReviewExportStampCount]: number
  [MonitoringContextKey.PlanReviewExportTextAnnotationCount]: number
  [MonitoringContextKey.PlanReviewExportTotalFileCount]: number
  [MonitoringContextKey.MyOrganization]: {
    id: number
    uuid: string
    displayName: string | null | undefined
    city: string
    slug: string
  }
  [MonitoringContextKey.Recipients]: (
    | {
        userId: number
      }
    | {
        email: string
      }
  )[]
  [MonitoringContextKey.RecordPlansSetReviewId]: number | undefined
  [MonitoringContextKey.RecordTypeId]: number
  [MonitoringContextKey.SettlementId]: number
}

type MonitoringEventPayload = {
  [MonitoringEvent.AiHotTopicClicked]: [
    MonitoringContextKey.AiChatSessionId,
    MonitoringContextKey.AiTopicId,
  ]
  [MonitoringEvent.AutoCheckEnableForApplicantsBannerDismissed]: [MonitoringContextKey.RecordTypeId]
  [MonitoringEvent.AutoCheckEnableForApplicantsBannerEnableClicked]: [
    MonitoringContextKey.RecordTypeId,
  ]
  [MonitoringEvent.AutoCheckEnableForApplicantsBannerShown]: [MonitoringContextKey.RecordTypeId]
  [MonitoringEvent.AutoCheckEnableForApplicantsFeedbackSubmitted]: [
    MonitoringContextKey.RecordTypeId,
    MonitoringContextKey.AutoCheckEnableForApplicantsSource,
  ]
  [MonitoringEvent.AutoCheckEnableForApplicantsModalDismissed]: [
    MonitoringContextKey.RecordTypeId,
    MonitoringContextKey.AutoCheckEnableForApplicantsSource,
  ]
  [MonitoringEvent.AutoCheckEnableForApplicantsModalEnableClicked]: [
    MonitoringContextKey.RecordTypeId,
    MonitoringContextKey.AutoCheckEnableForApplicantsSource,
  ]
  [MonitoringEvent.AutoCheckEnableForApplicantsModalShown]: [
    MonitoringContextKey.RecordTypeId,
    MonitoringContextKey.AutoCheckEnableForApplicantsSource,
  ]
  [MonitoringEvent.AutoCheckFeedbackAddAsCommentClicked]: [
    MonitoringContextKey.AutoCheckFeedbackItemId,
    MonitoringContextKey.RecordPlansSetReviewId,
  ]
  [MonitoringEvent.AutoCheckFeedbackAddedAsComment]: [
    MonitoringContextKey.AutoCheckFeedbackItemId,
    MonitoringContextKey.RecordPlansSetReviewId,
  ]
  [MonitoringEvent.AutoCheckFeedbackContentModifiedInComment]: [
    MonitoringContextKey.AutoCheckFeedbackItemId,
    MonitoringContextKey.RecordPlansSetReviewId,
  ]
  [MonitoringEvent.AutoCheckFeedbackHovered]: [
    MonitoringContextKey.AutoCheckFeedbackItemId,
    MonitoringContextKey.RecordPlansSetReviewId,
  ]
  [MonitoringEvent.AutoCheckFeedbackMarkedNotApplicable]: [
    MonitoringContextKey.AutoCheckFeedbackItemId,
    MonitoringContextKey.AutoCheckFeedbackSource,
    MonitoringContextKey.RecordPlansSetReviewId,
  ]
  [MonitoringEvent.AutoCheckFeedbackNotApplicableClicked]: [
    MonitoringContextKey.AutoCheckFeedbackItemId,
    MonitoringContextKey.AutoCheckFeedbackSource,
    MonitoringContextKey.RecordPlansSetReviewId,
  ]
  [MonitoringEvent.AutoCheckFeedbackResolved]: [
    MonitoringContextKey.AutoCheckFeedbackItemId,
    MonitoringContextKey.AutoCheckFeedbackSource,
    MonitoringContextKey.RecordPlansSetReviewId,
  ]
  [MonitoringEvent.CreateModuleClick]: [MonitoringContextKey.ModuleType]
  [MonitoringEvent.EntityVersionSearchItemSelected]: [
    MonitoringContextKey.IsMultiSelect,
    MonitoringContextKey.IsEntityVersionMapMode,
    MonitoringContextKey.EntityVersionSearchMapModeType,
  ]
  [MonitoringEvent.EntityVersionSearchMapLaunch]: [
    MonitoringContextKey.IsMultiSelect,
    MonitoringContextKey.EntityVersionSearchMapModeType,
  ]
  [MonitoringEvent.MobileAppBannerDownloadClicked]: never
  [MonitoringEvent.OrganizationPageView]: [
    MonitoringContextKey.OrganizationByPathId,
    MonitoringContextKey.OrganizationByPathSlug,
  ]
  [MonitoringEvent.PlanReviewDiffModeLaunched]: never
  [MonitoringEvent.PlanReviewExportDownloaded]: [
    MonitoringContextKey.RecordPlansSetReviewId,
    MonitoringContextKey.PlanReviewExportFileCount,
    MonitoringContextKey.PlanReviewExportTotalFileCount,
    MonitoringContextKey.PlanReviewExportRawFilesOnly,
    MonitoringContextKey.PlanReviewExportIncludeComments,
    MonitoringContextKey.PlanReviewExportIncludeContacts,
    MonitoringContextKey.PlanReviewExportIncludeMeasurements,
    MonitoringContextKey.PlanReviewExportIncludeShapes,
    MonitoringContextKey.PlanReviewExportIncludeStamps,
    MonitoringContextKey.PlanReviewExportIncludeTextAnnotations,
    MonitoringContextKey.PlanReviewExportCommentCount,
    MonitoringContextKey.PlanReviewExportMeasurementCount,
    MonitoringContextKey.PlanReviewExportShapeCount,
    MonitoringContextKey.PlanReviewExportStampCount,
    MonitoringContextKey.PlanReviewExportTextAnnotationCount,
  ]
  [MonitoringEvent.PlanReviewExportModalOpened]: [
    MonitoringContextKey.RecordPlansSetReviewId,
    MonitoringContextKey.PlanReviewExportTotalFileCount,
    MonitoringContextKey.PlanReviewExportCommentCount,
    MonitoringContextKey.PlanReviewExportMeasurementCount,
    MonitoringContextKey.PlanReviewExportShapeCount,
    MonitoringContextKey.PlanReviewExportStampCount,
    MonitoringContextKey.PlanReviewExportTextAnnotationCount,
  ]
  [MonitoringEvent.PlanReviewExportPrinted]: [
    MonitoringContextKey.RecordPlansSetReviewId,
    MonitoringContextKey.PlanReviewExportFileCount,
    MonitoringContextKey.PlanReviewExportTotalFileCount,
    MonitoringContextKey.PlanReviewExportRawFilesOnly,
    MonitoringContextKey.PlanReviewExportIncludeComments,
    MonitoringContextKey.PlanReviewExportIncludeContacts,
    MonitoringContextKey.PlanReviewExportIncludeMeasurements,
    MonitoringContextKey.PlanReviewExportIncludeShapes,
    MonitoringContextKey.PlanReviewExportIncludeStamps,
    MonitoringContextKey.PlanReviewExportIncludeTextAnnotations,
    MonitoringContextKey.PlanReviewExportCommentCount,
    MonitoringContextKey.PlanReviewExportMeasurementCount,
    MonitoringContextKey.PlanReviewExportShapeCount,
    MonitoringContextKey.PlanReviewExportStampCount,
    MonitoringContextKey.PlanReviewExportTextAnnotationCount,
  ]
  [MonitoringEvent.PlanReviewOpened]: [
    MonitoringContextKey.RecordPlansSetReviewId,
    MonitoringContextKey.PendingAutoCheckItemCount,
  ]
  [MonitoringEvent.SettlementReportDownloaded]: [MonitoringContextKey.SettlementId]
  [MonitoringEvent.SettlementReportShared]: [
    MonitoringContextKey.SettlementId,
    MonitoringContextKey.Recipients,
  ]
}

// Singleton that abstracts away the individual 3rd party providers from the rest of the app
export const monitoring = Object.freeze({
  captureEvent: <TEvent extends MonitoringEvent>(
    event: TEvent,
    ...payloadArgs: MonitoringEventPayload[TEvent] extends never
      ? []
      : [
          {
            [key in MonitoringEventPayload[TEvent][number]]: MonitoringContextValue[key]
          },
        ]
  ) => {
    posthog.capture(event, ...payloadArgs)
  },
  logError: (error: unknown, context?: Record<string, unknown>) => {
    datadogRum.addError(error, context)
  },
  setGlobalContextValue: <TKey extends MonitoringContextKey>(
    key: TKey,
    value: MonitoringContextValue[TKey]
  ) => {
    datadogRum.setGlobalContextProperty(key, value)
  },
})

export const useMonitoring = (contextData: AppStateContextData) => {
  const location = useLocation()
  const { sessionId } = usePersistentIds()
  const { mutateAsync: logSentEmailActivityMutation } = useLogSentEmailActivityMutation()

  useEffect(() => {
    // Initialize DataDog
    if (EnvVariables.DatadogRumEnabled === 'false' || !EnvVariables.DatadogClientToken) {
      return
    }
    datadogRum.init({
      applicationId: DatadogSettings.datadogApplicationId,
      clientToken: EnvVariables.DatadogClientToken,
      site: DatadogSettings.datadogSite,
      service: EnvVariables.DatadogService,
      env: EnvVariables.DatadogEnv,
      version: EnvVariables.VercelDeploymentSha,
      allowedTracingUrls: [
        // IMPORTANT!
        // We do not use https://*.govwell.com because we have Clerk proxied
        // at clerk.govwell.com, which doesn't accept the Datadog headers!
        // changing this without caution can cause an outage!
        // Check Cloudflare for any other services that could be affected by
        // changing this!
        /https:\/\/(app|dev|demo)\.govwell\.com/,
        /https:\/\/ai-chat-bot-app(\.(dev|demo))?\.govwell\.io/,
      ],
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'allow',
      storeContextsAcrossPages: true,
    })
  }, [])

  const { actor } = useAuth()
  const { user: clerkUser } = useUser()
  const govwellUser = contextData?.user
  const myOrg = contextData?.organization
  const isAppStateLoading = contextData?.isLoading

  const searchParams = new URLSearchParams(location.search)
  const sentEmailUuid = searchParams.get('sentEmailUuid')
  const userId = govwellUser?.id ?? null

  useEffect(() => {
    if (!sentEmailUuid || !sessionId || isAppStateLoading) {
      return
    }

    void logSentEmailActivityMutation({
      input: {
        sentEmailUuid,
        sessionId,
        userId,
      },
    })
  }, [logSentEmailActivityMutation, sessionId, sentEmailUuid, userId, isAppStateLoading])

  useEffect(() => {
    // Set user data
    const name = clerkUser?.fullName
    const email = clerkUser?.primaryEmailAddress?.emailAddress
    if (email && name) {
      const wasImpersonatedByUserWithId = actor?.sub
      const wasImpersonatedByUserWithEmail = actor?.email
      const userType = govwellUser?.type
      const userGroup = govwellUser?.group

      const commonProps = {
        id: clerkUser?.id,
        name,
        ...(wasImpersonatedByUserWithId ? { wasImpersonatedByUserWithId } : {}),
        ...(wasImpersonatedByUserWithEmail ? { wasImpersonatedByUserWithEmail } : {}),
        ...(userType ? { userType } : {}),
        ...(userGroup ? { userGroup } : {}),
      }

      datadogRum.setUser({
        ...commonProps,
        email,
      })

      // Set PostHog user
      posthog.identify(email, {
        ...commonProps,
        ...(myOrg ? { organizationId: myOrg.id, slug: myOrg.slug } : {}),
      })
    }
  }, [clerkUser, actor?.sub, actor?.email, myOrg, govwellUser])

  useEffect(() => {
    // Set my org data
    if (myOrg) {
      const myOrgContext = {
        id: myOrg.id,
        uuid: myOrg.uuid,
        displayName: myOrg.displayName,
        city: myOrg.city,
        slug: myOrg.slug,
      }

      datadogRum.setGlobalContextProperty(MonitoringContextKey.MyOrganization, myOrgContext)
    }
  }, [myOrg])

  const organizationByPath = contextData?.organizationByPath
  useEffect(() => {
    if (organizationByPath) {
      monitoring.captureEvent(MonitoringEvent.OrganizationPageView, {
        [MonitoringContextKey.OrganizationByPathId]: organizationByPath.id,
        [MonitoringContextKey.OrganizationByPathSlug]: organizationByPath.slug,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we only want to capture the event when the org ID changes
  }, [organizationByPath?.id])
}

export const resetMonitoring = (isLogin?: boolean) => {
  datadogRum.clearUser()
  datadogRum.setGlobalContext({})

  // In order to stitch unauthenticated sessions with authenticated sessions, we don't want to reset the session when logging in
  if (!isLogin) {
    posthog.reset(true) // resets the user and session
  }
}
